import { useOnboardingContext } from '@/features/onboarding-v2/context/OnboardingContext';
import { useSelectedSpecialtyCanonicalName } from '@/features/onboarding-v2/hooks/useSelectedSpecialtyCanonicalName';
import { useQuerySpecialtyOnboardingV2Assets } from '@/hooks/query/useQuerySpecialtyOnboardingV2Assets';
import type { DetailLevel } from '@heidi/growth';
import {
  type LearnBasicsDocumentOption,
  useLearnBasicsDocumentOptions,
} from '@heidi/growth';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { learnBasicsTaskMessages } from './useLearnBasicsTaskActions.messages';

// Generic fallbacks shown only while the per-specialty assets load (or if the
// asset query fails); replaced by the three real `evidence_queries` once loaded.
const EVIDENCE_QUESTION_FALLBACKS = [
  learnBasicsTaskMessages.evidenceQuestionFallback1,
  learnBasicsTaskMessages.evidenceQuestionFallback2,
  learnBasicsTaskMessages.evidenceQuestionFallback3,
];

export interface LearnBasicsTaskActions {
  evidenceQuestions: string[];
  documentOptions: LearnBasicsDocumentOption[];
  /**
   * The preview is still writing the note from the transcript. The tasks all
   * mutate that note, so their controls have nothing settled to act on yet —
   * callers should disable them (and Continue) until it lands.
   */
  isNoteGenerating: boolean;
  selectDetailLevel: (level: DetailLevel) => void;
  askEvidence: (index: number, question: string) => void;
  toggleDocument: (index: number, slug: string) => void;
}

/**
 * The three Learn-the-basics note tasks — detail level, evidence query, create
 * document — as data mutations, independent of how they are presented.
 *
 * Shared by the accordion (`LearnBasicsSteppedStep`, control arm) and the three
 * guided-tour screens (`TourDetailStep` / `TourArtefactsStep` /
 * `TourEvidenceStep`, treatment arm). The experiment must isolate presentation,
 * so both arms have to write identical context state and fire identical
 * analytics; keeping one implementation makes that true by construction rather
 * than by two copies staying in sync.
 */
export function useLearnBasicsTaskActions(): LearnBasicsTaskActions {
  const { formatMessage, locale } = useIntl();
  const { micTest, analytics } = useOnboardingContext();
  const { editActivity, setNoteTab } = micTest;

  const specialtyAssetName = useSelectedSpecialtyCanonicalName();
  const { data: assets } = useQuerySpecialtyOnboardingV2Assets(
    specialtyAssetName,
    locale
  );
  // The three real suggested questions for this specialty; fall back to the
  // generic copy only while assets are loading or unavailable.
  const evidenceQuestions = useMemo<string[]>(() => {
    const real = assets?.evidence_queries;
    if (real && real.length > 0) return real.slice(0, 3);
    return EVIDENCE_QUESTION_FALLBACKS.map((message) => formatMessage(message));
  }, [assets?.evidence_queries, formatMessage]);

  const documentOptions = useLearnBasicsDocumentOptions(assets?.documents);

  const selectDetailLevel = (level: DetailLevel) => {
    micTest.setDetailLevel(level);
    if (!editActivity.updatedStyle) {
      analytics.trackNoteStyleUpdated(true);
      micTest.setEditActivity({ ...editActivity, updatedStyle: true });
    }
  };

  // Optimistic completion: mark the task on tap (in addition to opening the
  // tab + analytics) so Continue enables immediately, independent of the
  // preview's tab animation (which keys off evidence/documentRequested).
  const askEvidence = (index: number, question: string) => {
    setNoteTab('evidence');
    micTest.setEvidenceQueryIndex(index);
    micTest.setEvidenceRequested(true);
    analytics.trackEvidenceQueried({
      queryType: 'suggested',
      queryLength: question.length,
    });
    if (!editActivity.usedEvidence) {
      micTest.setEditActivity({ ...editActivity, usedEvidence: true });
    }
  };

  // Multi-select: checking a document opens its own tab + generates it;
  // unchecking removes it. `createdDocument` completes while ≥ 1 is selected.
  const toggleDocument = (index: number, slug: string) => {
    const current = micTest.selectedDocuments;
    const isSelected = current.includes(index);
    const next = isSelected ? current.filter((i) => i !== index) : [...current, index];
    micTest.setSelectedDocuments(next);
    if (!isSelected) {
      setNoteTab(`doc-${index}`);
      analytics.trackDocumentCreated(slug);
    } else if (micTest.noteTab === `doc-${index}`) {
      setNoteTab('note');
    }
    micTest.setEditActivity({ ...editActivity, createdDocument: next.length > 0 });
  };

  return {
    evidenceQuestions,
    documentOptions,
    isNoteGenerating: micTest.phase === 'generating',
    selectDetailLevel,
    askEvidence,
    toggleDocument,
  };
}
